import { useReactiveFeatureFlagValue } from "@/app/contexts";

/**
 * LaunchDarkly JSON flag `language-menu` (SDK camelCase: `languageMenu`).
 *
 * ```json
 * { "timepilot": false, "heliotrace": false }
 * { "timepilot": true, "heliotrace": false }
 * { "timepilot": false, "heliotrace": true }
 * { "timepilot": true, "heliotrace": true }
 * ```
 *
 * A product key must be `true` to show that product's language menu. A missing
 * flag, a non-object value, or a non-boolean key hides the menu.
 */
export const LANGUAGE_MENU_FLAG_KEY = "language-menu";
export const LANGUAGE_MENU_FLAG_CAMEL = "languageMenu";

export type LanguageMenuProduct = "timepilot" | "heliotrace";

export type LanguageMenuFlag = Record<LanguageMenuProduct, boolean>;

export function parseLanguageMenuFlag(value: unknown): LanguageMenuFlag {
  const source =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  return {
    timepilot: source.timepilot === true,
    heliotrace: source.heliotrace === true,
  };
}

export function isLanguageMenuEnabled(value: unknown, product: LanguageMenuProduct): boolean {
  return parseLanguageMenuFlag(value)[product];
}

export function useLanguageMenuFlagValue(): unknown {
  const camel = useReactiveFeatureFlagValue(LANGUAGE_MENU_FLAG_CAMEL);
  const kebab = useReactiveFeatureFlagValue(LANGUAGE_MENU_FLAG_KEY);
  return camel ?? kebab;
}

export function useLanguageMenuEnabled(product: LanguageMenuProduct): boolean {
  return isLanguageMenuEnabled(useLanguageMenuFlagValue(), product);
}
